<template>
	<div class="change-contacts-wrapper">
		<div class="change-contacts">
			<FallBack
				v-if="error"
				:message="error"
				transparentBackground
				iconStyle="fal"
				icon="exclamation-triangle"
			/>
			<div v-if="!error">
				<p
					v-if="!singleContact"
					class="change-contacts-text"
					data-test="change-contacts-text"
				>
					You have selected {{ selectedContacts }} contact{{ plural }}.
				</p>
				<FormDropdowns
					label="Change Contact Type to"
					name="contact-type"
					class="change-contacts-dropdown"
					v-model="contactTypeId"
					:options="contactTypeOptions"
				/>
				<div class="error-container">
					<div class="error-spacer"></div>
					<div v-if="showError" class="show-error">
						<span class="error-text" data-test="error-text"
							>Select a contact type</span
						>
					</div>
				</div>
			</div>
		</div>
		<OverviewModalFooter
			isSaveModal
			:waiting="waiting"
			:success="success"
			@close="$emit('handleClose')"
			@handleClick="updateContacts()"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import OverviewModalFooter from './OverviewModalFooter.vue';
import FallBack from '@/components/global/FallBack.vue';
import FormDropdowns from '@/components/global/FormDropdowns.vue';
import leadsService from '@/services/contacts/leads-service.js';

export default {
	name: 'UpdateContactType',
	components: {
		OverviewModalFooter,
		FallBack,
		FormDropdowns,
	},
	props: {
		companyIds: Array,
		leadId: Number,
		selectAllMode: Boolean,
		selectedLeadIds: Array,
	},
	data() {
		return {
			contactTypeId: null,
			waiting: false,
			showError: false,
			success: false,
			error: null,
		};
	},
	computed: {
		...mapState({
			filters: (state) => state.ccContacts.filters,
			selectedBrokers: (state) => state.clientConnectV2.selectedContactsIds,
			totalCount: (state) => state.ccContacts.leads.totalCount,
		}),
		...mapGetters(['contactTypeOptions']),
		contactTypeValue() {
			let index = this.contactTypeOptions.findIndex((option) => {
				return option.value === this.contactTypeId;
			});
			if (index !== -1) {
				return this.contactTypeOptions[index].name;
			}
			return null;
		},
		contactsToRemove() {
			if (this.leadId) return [this.leadId];
			return this.selectedLeadIds;
		},
		currentFilters() {
			const [entityId, brokerIds] = this.companyIds;
			let filters = {
				data: {
					...this.filters,
					entityId,
				},
			};
			filters.searchTerm = filters.data.searchTerm;
			delete filters.data.searchTerm;

			if (this.selectedBrokers.length === 0) {
				filters.data.contactsIds = brokerIds;
			} else {
				filters.data.contactsIds = this.selectedBrokers;
			}
			return filters;
		},
		selectAll() {
			if (this.leadId) return false;
			return this.selectAllMode;
		},
		selectedContacts() {
			if (this.selectAllMode) {
				return this.totalCount - this.selectedLeadIds.length;
			}
			return this.selectedLeadIds.length;
		},
		singleContact() {
			return this.leadId || this.selectedContacts === 1;
		},
		plural() {
			return this.selectedContacts > 1 ? 's' : '';
		},
	},
	methods: {
		...mapActions(['editContactProperty']),
		async updateContacts() {
			if (this.contactTypeId === null) {
				this.showError = true;
				return;
			}
			let payload = {};

			if (!this.selectAll) {
				payload = {
					leadIds: this.contactsToRemove,
					leadTypeId: this.contactTypeId,
					selectAllMode: this.selectAll,
				};
			} else {
				payload = {
					leadIds: this.contactsToRemove,
					leadTypeId: this.contactTypeId,
					selectAllMode: this.selectAll,
					filters: this.currentFilters,
				};
			}

			this.waiting = true;
			try {
				await leadsService.updateMultipleLeads(payload);
				this.success = true;

				setTimeout(() => {
					if (this.selectedLeadIds && this.leadId) {
						this.$emit('filterLeadId');
					} else {
						this.$emit('clearLeadIds');
					}
					this.editContactProperty({
						prop: 'type',
						value: this.contactTypeValue,
						leadIds: this.contactsToRemove,
						selectAll: this.selectAll,
					});
					this.success = false;
				}, 1500);
			} catch (error) {
				this.error = error.message;
			}
			this.waiting = false;
		},
	},
	watch: {
		contactTypeId(newValue) {
			if (newValue !== null) {
				this.showError = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.change-contacts-wrapper {
	.change-contacts {
		@include rem('padding', 20px);
	}

	p.change-contacts-text {
		margin: 0;
		padding: 0;
		padding-bottom: 40px;
		color: $b360-color--blue;
		font-weight: bold;
		@include rem('line-height', 25px);
	}

	.cc-link {
		@include typo--roboto__medium;
		color: $brand-blue;
		text-decoration: none;
	}
}

.bolded-message-text {
	@include typo--roboto__bold;
}

.change-contacts-dropdown {
	display: flex;
	align-items: center;
	column-gap: 10px;
	flex-wrap: wrap;
	justify-content: space-between;
	white-space: nowrap;

	& /deep/ .b360-form-input--select {
		width: clamp(200px, 40vw, 275px);
	}
}

.error-container {
	display: flex;
	align-items: center;
	column-gap: 10px;
	flex-wrap: wrap;
	justify-content: space-between;
	white-space: nowrap;

	.error-spacer {
		width: 171.44px;
	}

	.show-error {
		width: clamp(200px, 40vw, 270px);

		.error-text {
			color: $error;
			padding: 0;
			width: clamp(200px, 40vw, 270px);
			line-height: 36px;
		}
	}
}
</style>
